import showLinkContextMenu from './LinkContextMenu';
import ReactComment from './ReactComment';
import { updateComposeLinkViewState } from '../actions/internalActions';
import getComposeLinkViewState from '../selectors/getComposeLinkViewState';
import ComposeLinkViewState from '../store/schema/ComposeLinkViewState';
import { OWA_AUTO_LINKS_CLASSNAME } from '../utils/constants';
import { observer } from 'mobx-react';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FileIcon, getIconForFile } from 'owa-file-icon';
import { InfoBarMessageRank } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import {
    getSharingDataFromLink,
    lazyGetSharingTipRecipientInfo,
    SharingLinkInfo,
} from 'owa-link-data';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import getPackageBaseUrl from 'owa-session-store/lib/actions/getPackageBaseUrl';
import { lazyGetSharingIssuesForSharingData, SharingTipRecipientInfo } from 'owa-sharing-data';
import { format } from 'owa-strings';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Editor } from 'roosterjs-editor-core';
import { PositionType } from 'roosterjs-editor-types';
import {
    COMPOSE_ONLY_ATTACHMENT_CONTENT_END,
    COMPOSE_ONLY_ATTACHMENT_CONTENT_START,
} from 'owa-attachment-link-common';

const TEXT_DIRECTORY_MIME_TYPE = 'text/directory';
const CONTEXT_MENU_EVENT_NAME = 'contextmenu';
const CLICK_EVENT_NAME = 'click';
const ATTRIBUTE_STYLE_NAME = 'style';
const ATTRIBUTE_CONTENTEDITABLE_NAME = 'contenteditable';
const STYLE_SEPARATOR = ';';
const SPAN_TAG_NAME = 'SPAN';

interface SharingLinkProps {
    viewState: ComposeLinkViewState;
    recipientWells: RecipientWellWithFindControlViewState[];
    fromAddress: string;
    sharingLink: SharingLinkInfo;
    fileIcon: string;
    hasValidFileName: boolean;
    editor: Editor;
    originalAnchor: HTMLAnchorElement;
    shouldTryShowContextMenuOnMount: boolean;
    composeId: string;
    isLinkPreviouslyBeautified: boolean;
}

const linkStyles = require('./Link.scss');
const classNames = require('classnames/bind').bind(linkStyles);

enum SharingIssueLevel {
    None,
    Warning,
    Error,
}

@observer
class Link extends React.Component<SharingLinkProps, {}> {
    private link: HTMLAnchorElement;

    render() {
        const sharingIssueLevel: SharingIssueLevel = this.props.viewState
            ? getHasSharingIssues(
                  this.props.viewState.linkId,
                  this.props.viewState.isLinkBeautified,
                  this.props.recipientWells,
                  this.props.fromAddress
              )
            : SharingIssueLevel.None;
        return (
            <a
                tabIndex={-1}
                ref={ref => (this.link = ref)}
                href={this.props.sharingLink.url}
                onMouseOver={this.onMouseOver}
                onMouseLeave={this.onMouseLeave}
                style={this.getAnchorStyle()}
                id={this.props.sharingLink.linkId}
                className={classNames(
                    linkStyles.link,
                    OWA_AUTO_LINKS_CLASSNAME,
                    this.getAnchorTextColor(sharingIssueLevel)
                )}>
                {this.props.viewState.isLinkBeautified && this.props.hasValidFileName && (
                    <img
                        src={this.props.fileIcon}
                        style={this.getFileIconStyle()}
                        alt=""
                        role="presentation"
                    />
                )}
                {this.props.viewState.displayName}
                <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_START} />
                <img
                    alt=""
                    role="presentation"
                    src={this.getErrorIcon(sharingIssueLevel)}
                    hidden={sharingIssueLevel === SharingIssueLevel.None}
                    style={this.getErrorIconStyle()}
                />
                <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_END} />
            </a>
        );
    }

    componentDidMount() {
        this.copyStyleFromOriginalAnchor();

        // Make beautiful link readonly & handle the click event
        if (this.props.viewState.isLinkBeautified) {
            this.link.setAttribute(ATTRIBUTE_CONTENTEDITABLE_NAME, 'false');
            this.link.addEventListener(CLICK_EVENT_NAME, this.onClick);
        }
        this.link.addEventListener(CONTEXT_MENU_EVENT_NAME, this.onContextMenu);

        // We need to replace the child only so we keep any text typed under the parent element before the new anchor
        this.props.editor.replaceNode(this.props.originalAnchor, this.link);

        // When its a new pasted link, guarantee the cursor is after the pasted link and open the context menu. If opening a draft, do not set the cursor location and do not open the context menu.
        const selectionRange = this.props.editor.getSelectionRange();
        if (selectionRange) {
            if (
                selectionRange.startContainer === this.link.parentNode &&
                selectionRange.endContainer === this.link.parentNode
            ) {
                this.props.editor.select(this.link, PositionType.After);
                if (this.props.shouldTryShowContextMenuOnMount) {
                    showLinkContextMenu(
                        this.link,
                        this.props.editor,
                        this.props.composeId,
                        this.props.recipientWells,
                        this.props.fromAddress
                    );
                }
            }
        }
    }

    componentWillUnmount() {
        if (this.link) {
            if (this.props.viewState.isLinkBeautified) {
                this.link.removeEventListener(CLICK_EVENT_NAME, this.onClick);
            }

            this.link.removeEventListener(CONTEXT_MENU_EVENT_NAME, this.onContextMenu);
        }
    }

    private copyStyleFromOriginalAnchor() {
        // Pass the style from old link to new link
        let style: string = this.props.originalAnchor.getAttribute(ATTRIBUTE_STYLE_NAME);
        if (style && style.length > 0 && style.lastIndexOf(STYLE_SEPARATOR) !== style.length - 1) {
            style += STYLE_SEPARATOR;
        }
        style += this.link.getAttribute(ATTRIBUTE_STYLE_NAME);

        // If the original anchor is a beautiful link, we need to check whether it has a span defining the font-size.
        if (this.props.isLinkPreviouslyBeautified) {
            const spanElement = this.props.originalAnchor.querySelector(SPAN_TAG_NAME);
            if (spanElement) {
                const spanStyle = this.props.originalAnchor.children[1].getAttribute(
                    ATTRIBUTE_STYLE_NAME
                );
                style += spanStyle;
            }
        }

        this.link.setAttribute(ATTRIBUTE_STYLE_NAME, style);
    }

    private onContextMenu = (event: MouseEvent) => {
        showLinkContextMenu(
            this.link,
            this.props.editor,
            this.props.composeId,
            this.props.recipientWells,
            this.props.fromAddress
        );
        event.preventDefault();
    };

    private onClick = (event: MouseEvent) => {
        showLinkContextMenu(
            this.link,
            this.props.editor,
            this.props.composeId,
            this.props.recipientWells,
            this.props.fromAddress
        );
        event.preventDefault();
    };

    private getAnchorStyle = (): React.CSSProperties => {
        if (this.props.viewState.isLinkBeautified) {
            // Highlighted Background
            return {
                padding: '0 1px',
                backgroundColor: 'rgb(244,244,244)',
                borderRadius: '2px',
                userSelect: 'all',
            };
        } else {
            return null;
        }
    };

    private getFileIconStyle = () => {
        return {
            width: '16px',
            height: '16px',
            verticalAlign: 'middle',
            padding: '1px 2px 2px 0',
        };
    };

    private getErrorIconStyle = () => {
        return {
            width: '12px',
            height: '12px',
            verticalAlign: 'top',
        };
    };

    private getAnchorTextColor = (sharingIssueLevel: SharingIssueLevel) => {
        if (sharingIssueLevel === SharingIssueLevel.Error) {
            return linkStyles.linkWithSharingIssue;
        }
    };

    private getErrorIcon = (shairngIssueLevel: SharingIssueLevel) => {
        if (shairngIssueLevel === SharingIssueLevel.Error) {
            return format('{0}{1}', getPackageBaseUrl(), 'resources/images/redinfo_24.png');
        } else if (shairngIssueLevel === SharingIssueLevel.Warning) {
            return format('{0}{1}', getPackageBaseUrl(), 'resources/images/greyinfo_24.png');
        }
        return '';
    };

    /*
    We have to use inline CSS style: background-color for it to work in different clients such as gmail.
    Since inline CSS style has higher preference than class, we can't use :hover as it would be just ignored.
    So we have to hook to these two events to change the hover background color.
    */
    private onMouseOver = () => {
        if (this.link && this.props.viewState.isLinkBeautified) {
            this.link.style.backgroundColor = 'rgb(237,235,233)';
        }
    };

    private onMouseLeave = () => {
        if (this.link && this.props.viewState.isLinkBeautified) {
            this.link.style.backgroundColor = 'rgb(243,242,241)';
        }
    };
}

export function createSharingLinkView(
    editor: Editor,
    recipientWells: RecipientWellWithFindControlViewState[],
    fromAddress: string,
    composeId: string,
    anchorElement: HTMLAnchorElement,
    sharingLinkInfo: SharingLinkInfo,
    sharingLinkInfoHasValidFileName: boolean,
    mimeType: string,
    shouldBeautify: boolean,
    isLinkPreviouslyBeautified: boolean,
    shouldTryShowContextMenuOnMount: boolean = false
) {
    const shouldShowBeautifiedLink = shouldBeautify || isLinkPreviouslyBeautified;
    const hasValidFileName = sharingLinkInfoHasValidFileName || isLinkPreviouslyBeautified; // Previous beautified link has the file name as the inner text

    // to get the file icon, use the original name that came from the response, as the text filename could have been modified for display
    const fileIcon = getFileIcon(
        isLinkPreviouslyBeautified ? anchorElement.innerText : sharingLinkInfo.fileName,
        shouldShowBeautifiedLink,
        mimeType
    );

    const anchorContainer = document.createElement('span');
    const displayName = getDisplayName(
        anchorElement,
        sharingLinkInfo,
        hasValidFileName,
        shouldBeautify,
        isLinkPreviouslyBeautified
    );

    updateComposeLinkViewState(anchorElement.id, displayName, shouldShowBeautifiedLink);

    ReactDOM.render(
        <Link
            viewState={getComposeLinkViewState(anchorElement.id)}
            recipientWells={recipientWells}
            fromAddress={fromAddress}
            sharingLink={sharingLinkInfo}
            fileIcon={fileIcon}
            hasValidFileName={hasValidFileName}
            editor={editor}
            originalAnchor={anchorElement}
            shouldTryShowContextMenuOnMount={shouldTryShowContextMenuOnMount}
            composeId={composeId}
            isLinkPreviouslyBeautified={isLinkPreviouslyBeautified}
        />,
        anchorContainer
    );
}

function getHasSharingIssues(
    linkId: string,
    isLinkBeautified: boolean,
    recipientWells: RecipientWellWithFindControlViewState[],
    fromAddress
): SharingIssueLevel {
    const sharingData = getSharingDataFromLink(linkId);

    // We do not check for sharing issues unless the link is beautified and the feature is enabled
    if (
        sharingData === null ||
        !isLinkBeautified ||
        !isFeatureEnabled('doc-attachment-contextualSharingTips')
    ) {
        return SharingIssueLevel.None;
    }
    const getSharingTipRecipientInfo = lazyGetSharingTipRecipientInfo.tryImportSync();
    const getSharingIssuesForSharingData = lazyGetSharingIssuesForSharingData.tryImportSync();

    let recipientInfo: SharingTipRecipientInfo[] = [];
    if (getSharingTipRecipientInfo && getSharingIssuesForSharingData) {
        recipientInfo = getSharingTipRecipientInfo(recipientWells, fromAddress);

        const sharingIssues = getSharingIssuesForSharingData(sharingData, recipientInfo);
        if (sharingIssues && sharingIssues.length > 0) {
            if (sharingIssues.some(issue => issue.messageRank === InfoBarMessageRank.Error)) {
                return SharingIssueLevel.Error;
            }
            return SharingIssueLevel.Warning;
        }
    }

    return SharingIssueLevel.None;
}

function getDisplayName(
    anchorElement: HTMLAnchorElement,
    sharingLinkInfo: SharingLinkInfo,
    hasValidFileName: boolean,
    shouldBeautify: boolean,
    isLinkPreviouslyBeautified: boolean
): string {
    if (isLinkPreviouslyBeautified) {
        return anchorElement.innerText;
    }

    if (hasValidFileName && shouldBeautify) {
        return sharingLinkInfo.fileName;
    }

    return anchorElement.text;
}

function getFileIcon(fileName: string, shouldBeautify: boolean, mimeType: string): string {
    if (fileName && shouldBeautify) {
        const fileIcon: FileIcon = getIconForFile(
            fileName,
            mimeType === TEXT_DIRECTORY_MIME_TYPE,
            mimeType
        );
        return fileIcon.cdn || '';
    }

    return '';
}
